/**
 * `/api/reference` — fetched ONCE at app start and never refetched (Phase 4
 * constraint: "Use `/api/reference` once at app start; do not refetch
 * dropdowns").
 *
 * It is TanStack Query's cache seed. `staleTime: Infinity` is the whole
 * mechanism: every dropdown in the product reads from this one entry, so a
 * stage list, a reason-code list and a product list cost zero requests after
 * the first.
 *
 * An Admin editing reference data invalidates it explicitly
 * (`REFERENCE_QUERY_KEY`), which is the only way it is ever refetched.
 */

import { useMemo } from 'react';
import type { ReferenceData } from '@menlo/shared';
import { useApiQuery } from '@/lib/queries';
import type { ApiError } from '@/lib/api';
import { businessUnitLabel } from '@/lib/format';

export const REFERENCE_QUERY_KEY = ['reference'] as const;

export function useReference(): {
  data: ReferenceData | undefined;
  isLoading: boolean;
  error: ApiError | null;
} {
  const query = useApiQuery<ReferenceData>(REFERENCE_QUERY_KEY, '/api/reference', {
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return { data: query.data, isLoading: query.isLoading, error: query.error };
}

export type Stage = ReferenceData['stages'][number];
export type ReasonCode = ReferenceData['reason_codes'][number];
export type Product = ReferenceData['products'][number];
export type RepFirm = ReferenceData['rep_firms'][number];
export type BusinessUnitRef = ReferenceData['business_units'][number];
export type RefUser = ReferenceData['users'][number];

const EMPTY: ReferenceData = {
  stages: [],
  reason_codes: [],
  business_units: [],
  products: [],
  competitors: [],
  rep_firms: [],
  regions: ['AMER', 'EMEA', 'APAC'],
  users: [],
  export_sensitive_accounts: [],
  export_sensitive_programs: [],
  export_policy_version: 'unversioned',
  attachment_max_bytes: 0,
  attachment_content_type_allowlist: [],
  multipart_threshold_bytes: 100 * 1024 * 1024,
};

/** The reference payload, or an empty one — so a page renders rather than crashing. */
export function useReferenceData(): ReferenceData {
  const { data } = useReference();
  return data ?? EMPTY;
}

/**
 * Stage lookup by code, in `sort_order`.
 *
 * §5.1 [R3]: **nothing compares stage codes as strings.** Every ordering
 * question goes through `sort_order`, which is why this returns the whole row
 * and never a bare index.
 */
export function useStages(): { list: Stage[]; byCode: Map<string, Stage> } {
  const reference = useReferenceData();
  return useMemo(() => {
    const list = [...reference.stages].sort((a, b) => a.sort_order - b.sort_order);
    return { list, byCode: new Map(list.map((stage) => [stage.code, stage])) };
  }, [reference.stages]);
}

export function useUserLookup(): Map<string, RefUser> {
  const reference = useReferenceData();
  return useMemo(() => new Map(reference.users.map((user) => [user.id, user])), [reference.users]);
}

export function useProductLookup(): Map<string, Product> {
  const reference = useReferenceData();
  return useMemo(
    () => new Map(reference.products.map((product) => [product.id, product])),
    [reference.products],
  );
}

export function useRepFirmLookup(): Map<string, RepFirm> {
  const reference = useReferenceData();
  return useMemo(
    () => new Map(reference.rep_firms.map((firm) => [firm.id, firm])),
    [reference.rep_firms],
  );
}

/**
 * Reason codes for one outcome (§5.1 [R4]): `applies_to` carries the four
 * OUTCOME values — `won`, `lost`, `no_decision`, `design_win_lapsed` — never
 * loose labels like `win`/`loss`.
 *
 * `is_active = false` codes are excluded from the picker: `IMPORTED_CHANNEL`
 * exists so the importer can write a closure for a deal that arrived already
 * won, and a person must not be able to choose it. `WIN_LAPSED_NO_CAUSE` is
 * active precisely so a person CAN record a lapse with no known cause.
 */
export function reasonCodesFor(reference: ReferenceData, outcome: string): ReasonCode[] {
  return reference.reason_codes
    .filter((code) => code.is_active && code.applies_to.includes(outcome))
    .sort((a, b) => a.category.localeCompare(b.category) || a.label.localeCompare(b.label));
}

/**
 * Business units, from the `business_units` table (session 7). The one list:
 * no page keeps its own.
 *
 * - `list`: every BU in `sort_order`, retired included — history (snapshots,
 *   old lines) still needs their labels.
 * - `options`: what a filter or a picker offers. Retired BUs drop out.
 * - `label(code)`: the table's label, or the code humanized if it is unknown.
 */
export function useBusinessUnits(): {
  list: BusinessUnitRef[];
  options: BusinessUnitRef[];
  label: (code: string) => string;
} {
  const reference = useReferenceData();
  return useMemo(() => {
    const list = [...reference.business_units].sort(
      (a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code),
    );
    const byCode = new Map(list.map((unit) => [unit.code, unit]));
    return {
      list,
      options: list.filter((unit) => unit.status !== 'retired'),
      label: (code: string) => businessUnitLabel(code, byCode),
    };
  }, [reference.business_units]);
}
